/**
 * Pipelines · Tareas (listado global estilo HubSpot).
 *
 * Visibilidad por rol (resuelta en backend):
 *  - admin: todas las tareas de la plataforma
 *  - supervisor: las suyas + las de sus supervisados
 *  - usuario: solo las asignadas a él
 *
 * Filtros: Todas / Vencen hoy / Atrasado / Próximamente / Completadas.
 * Botón "Crear tarea" abre el panel lateral (asociando un negocio).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  CheckSquare, Plus, Search as SearchIcon, Loader2, CheckCircle2, Circle,
  Trash2, Phone, Mail, Ticket as TicketIcon, AlertTriangle, CalendarClock, CalendarDays, Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import CreateTaskPanel from "./CreateTaskPanel";
import TaskAttachmentsDialog from "./TaskAttachmentsDialog";

const SLA_CHIP = {
  en_tiempo: ["En tiempo", "bg-green-100 text-green-700 border-green-300"],
  por_vencer: ["Por vencer", "bg-amber-100 text-amber-700 border-amber-300"],
  vencida: ["Vencida", "bg-red-100 text-red-700 border-red-300"],
  cumplida: ["En SLA", "bg-emerald-100 text-emerald-700 border-emerald-300"],
  incumplida: ["Fuera SLA", "bg-rose-100 text-rose-700 border-rose-300"],
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_ICON = { por_hacer: CheckSquare, llamada: Phone, correo: Mail };
const TIPO_LABEL = { por_hacer: "Por hacer", llamada: "Llamada", correo: "Correo" };
const PRIO_STYLE = {
  alta: "bg-rose-100 text-rose-700 border-rose-200",
  media: "bg-amber-100 text-amber-700 border-amber-200",
  baja: "bg-slate-100 text-slate-600 border-slate-200",
  ninguna: "bg-slate-50 text-slate-400 border-slate-200",
};
const BUCKET_STYLE = {
  atrasado: "text-rose-600",
  hoy: "text-amber-600",
  proximamente: "text-slate-600",
  completada: "text-emerald-600",
  sin_fecha: "text-slate-400",
};

export default function TareasPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [slaFilter, setSlaFilter] = useState("");
  const [search, setSearch] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const [attachTask, setAttachTask] = useState(null);
  const [vistaSupervisor, setVistaSupervisor] = useState("");
  const [vistaSupervisorOpciones, setVistaSupervisorOpciones] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/pipelines/tasks`;
      if (vistaSupervisor) url += `?vista_supervisor_id=${encodeURIComponent(vistaSupervisor)}`;
      const r = await fetch(url, getAuthHeaders());
      const d = await r.json();
      setTasks(d.items || []);
    } catch { toast.error("Error al cargar tareas"); }
    setLoading(false);
  }, [getAuthHeaders, vistaSupervisor]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/pipelines/vista-supervisor/opciones`, getAuthHeaders());
        if (r.ok) {
          const d = await r.json();
          setVistaSupervisorOpciones(d.opciones || []);
        }
      } catch {}
    })();
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/users`, getAuthHeaders());
        const d = await r.json();
        setUsers(Array.isArray(d) ? d : (d.items || d.users || []));
      } catch {}
    })();
  }, [getAuthHeaders]);

  const toggle = async (t) => {
    const status = t.status === "completada" ? "pendiente" : "completada";
    const r = await fetch(`${API_URL}/api/pipelines/tasks/${t.id}`, {
      method: "PUT", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
      body: JSON.stringify({ ...t, status, task_status: status === "completada" ? "completada" : t.task_status }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || "No se pudo actualizar la tarea");
    }
    load();
  };
  const del = async (t) => {
    if (!window.confirm("¿Eliminar tarea?")) return;
    await fetch(`${API_URL}/api/pipelines/tasks/${t.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
    load();
  };

  const counts = useMemo(() => {
    const c = { all: tasks.length, hoy: 0, atrasado: 0, proximamente: 0, completada: 0 };
    tasks.forEach((t) => { if (c[t.due_bucket] !== undefined) c[t.due_bucket] += 1; });
    return c;
  }, [tasks]);

  const slaCounts = useMemo(() => {
    const c = { vencida: 0, por_vencer: 0, en_tiempo: 0 };
    tasks.forEach((t) => { if (c[t.sla_status] !== undefined) c[t.sla_status] += 1; });
    return c;
  }, [tasks]);

  const filtered = useMemo(() => {
    let list = tasks;
    if (tab !== "all") list = list.filter((t) => t.due_bucket === tab);
    if (slaFilter) list = list.filter((t) => t.sla_status === slaFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((t) =>
        (t.title || "").toLowerCase().includes(q) ||
        (t.card_title || "").toLowerCase().includes(q) ||
        (t.pipeline_name || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [tasks, tab, slaFilter, search]);

  const TABS = [
    { key: "all", label: "Todas", icon: CheckSquare, count: counts.all },
    { key: "hoy", label: "Vencen hoy", icon: CalendarClock, count: counts.hoy },
    { key: "atrasado", label: "Atrasado", icon: AlertTriangle, count: counts.atrasado },
    { key: "proximamente", label: "Próximamente", icon: CalendarDays, count: counts.proximamente },
    { key: "completada", label: "Completadas", icon: CheckCircle2, count: counts.completada },
  ];

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-64">
        <div className="max-w-[1500px] mx-auto p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-2xl font-bold text-[#193966] flex items-center gap-2">
                <CheckSquare className="w-6 h-6 text-[#80BC00]" /> Tareas
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">Gestiona las tareas de tus pipelines y negocios.</p>
            </div>
            <Button onClick={() => setPanelOpen(true)} className="bg-[#193966] hover:bg-[#122a4d] text-white" data-testid="tareas-create-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Crear tarea
            </Button>
          </div>

          {/* Filtros */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`tareas-tab-${t.key}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    active ? "bg-[#193966] text-white border-[#193966]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                  <span className={`text-[11px] px-1.5 rounded-full ${active ? "bg-white/20" : "bg-slate-100"}`}>{t.count}</span>
                </button>
              );
            })}
            <span className="h-5 w-px bg-slate-200 mx-1" />
            {[
              { key: "vencida", label: "Vencidas", count: slaCounts.vencida, active: "bg-red-600 text-white border-red-600", idle: "bg-red-50 text-red-700 border-red-200 hover:bg-red-100" },
              { key: "por_vencer", label: "Por vencer", count: slaCounts.por_vencer, active: "bg-amber-500 text-white border-amber-500", idle: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100" },
              { key: "en_tiempo", label: "En tiempo", count: slaCounts.en_tiempo, active: "bg-emerald-600 text-white border-emerald-600", idle: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" },
            ].map((f) => {
              const active = slaFilter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setSlaFilter(active ? "" : f.key)}
                  data-testid={`tareas-sla-filter-${f.key}`}
                  title={`Filtrar por SLA: ${f.label}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? f.active : f.idle}`}
                >
                  {f.label}
                  <span className={`text-[11px] px-1.5 rounded-full ${active ? "bg-white/20" : "bg-white/70"}`}>{f.count}</span>
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-2">
              {vistaSupervisorOpciones.length > 0 && (
                <select
                  value={vistaSupervisor}
                  onChange={(e) => setVistaSupervisor(e.target.value)}
                  className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-600"
                  data-testid="tareas-vista-supervisor-select"
                  title="Vista Supervisor: ver tareas del supervisor cubierto y sus supervisados"
                >
                  <option value="">Vista Supervisor: Mi vista</option>
                  {vistaSupervisorOpciones.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              )}
              <div className="relative">
                <SearchIcon className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar tarea o negocio…" className="pl-8 w-64" data-testid="tareas-search" />
              </div>
            </div>
          </div>

          {/* Tabla */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="py-2.5 px-3 text-left w-10"></th>
                  <th className="py-2.5 px-3 text-left">Tarea</th>
                  <th className="py-2.5 px-3 text-left">Tipo</th>
                  <th className="py-2.5 px-3 text-left">Prioridad</th>
                  <th className="py-2.5 px-3 text-left">Negocio / Pipeline</th>
                  <th className="py-2.5 px-3 text-left">Asignado</th>
                  <th className="py-2.5 px-3 text-left">Vence</th>
                  <th className="py-2.5 px-3 text-left">Ticket</th>
                  <th className="py-2.5 px-3 text-center w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={9} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto" /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={9} className="py-10 text-center text-slate-400 text-sm">No hay tareas en esta vista.</td></tr>
                ) : filtered.map((t) => {
                  const Icon = TIPO_ICON[t.task_type] || CheckSquare;
                  const done = t.status === "completada" || t.task_status === "completada";
                  return (
                    <tr key={t.id} className="hover:bg-slate-50" data-testid={`tarea-row-${t.id}`}>
                      <td className="py-2.5 px-3">
                        <button onClick={() => toggle(t)} data-testid={`tarea-toggle-${t.id}`}>
                          {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300" />}
                        </button>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${done ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</p>
                          {t.attachments_count > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-500" title={`${t.attachments_count} adjunto(s)`} data-testid={`tarea-attach-${t.id}`}>
                              <Paperclip className="w-3 h-3" />{t.attachments_count}
                            </span>
                          )}
                        </div>
                        {t.notes && <p className="text-[11px] text-slate-400 truncate max-w-[280px]">{t.notes}</p>}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                          <Icon className="w-3.5 h-3.5 text-slate-400" /> {TIPO_LABEL[t.task_type] || "Por hacer"}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        <Badge variant="outline" className={`text-[10px] capitalize ${PRIO_STYLE[t.priority] || PRIO_STYLE.ninguna}`}>{t.priority || "ninguna"}</Badge>
                      </td>
                      <td className="py-2.5 px-3">
                        <p className="text-slate-700 truncate max-w-[220px]">{t.card_title || "—"}</p>
                        {t.pipeline_name && <p className="text-[10px] text-slate-400">{t.pipeline_name}</p>}
                      </td>
                      <td className="py-2.5 px-3 text-slate-600">{t.assigned_to_name || "—"}</td>
                      <td className={`py-2.5 px-3 ${BUCKET_STYLE[t.due_bucket] || "text-slate-500"}`}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {t.due_date ? new Date(t.due_date).toLocaleDateString("es-DO") : "—"}
                          {SLA_CHIP[t.sla_status] && (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${SLA_CHIP[t.sla_status][1]}`} data-testid={`tarea-sla-${t.id}`}>
                              {SLA_CHIP[t.sla_status][0]}
                            </span>
                          )}
                        </div>
                        {t.task_type_name && <p className="text-[10px] text-slate-400">{t.task_type_name}</p>}
                      </td>
                      <td className="py-2.5 px-3">
                        {t.helpdesk_ticket_numero ? (
                          <button
                            type="button"
                            onClick={() => t.helpdesk_ticket_id && navigate(`/mesa-ayuda/tickets/${t.helpdesk_ticket_id}`)}
                            title="Ir al ticket en Mesa de Ayuda"
                            data-testid={`tarea-ticket-${t.id}`}
                          >
                            <Badge variant="outline" className="text-[10px] border-sky-300 text-sky-700 cursor-pointer hover:bg-sky-100 hover:underline">
                              <TicketIcon className="w-3 h-3 mr-1" /> #{t.helpdesk_ticket_numero}
                            </Badge>
                          </button>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <div className="flex items-center gap-0.5 justify-center">
                          <button onClick={() => setAttachTask(t)} className="p-1 hover:bg-slate-100 rounded text-slate-500" title="Adjuntos (minutas, informes…)" data-testid={`tarea-adjuntos-${t.id}`}>
                            <Paperclip className="w-4 h-4" />
                          </button>
                          <button onClick={() => del(t)} className="p-1 hover:bg-rose-50 rounded text-rose-500" data-testid={`tarea-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <CreateTaskPanel open={panelOpen} onOpenChange={setPanelOpen} users={users} onCreated={load} />
      {attachTask && <TaskAttachmentsDialog task={attachTask} onClose={() => setAttachTask(null)} onChanged={load} />}
    </div>
  );
}
